import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Plus, X, Moon } from "lucide-react";

const DISMISS_KEY = "dw-a2hs-dismissed-at";
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 4000;

function isDismissedRecently() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = parseInt(raw, 10);
  if (Number.isNaN(dismissedAt)) return false;
  return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export default function AddToHomeScreenPrompt() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState(null);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone || isDismissedRecently()) return;

    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);
    if (!isIOS && !isAndroid) return;

    let timer;
    if (isIOS) {
      setPlatform("ios");
      timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
      return () => clearTimeout(timer);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setPlatform("android");
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleInstall = async () => {
    const evt = deferredPromptRef.current;
    if (!evt) return;
    evt.prompt();
    try { await evt.userChoice; } catch { /* user dismissed native prompt */ }
    deferredPromptRef.current = null;
    dismiss();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="add-to-home-screen-prompt"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
          className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[80]"
        >
          <div className="glass-card rounded-2xl p-4 flex items-start gap-3 border border-amber-400/25 shadow-2xl shadow-black/50">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center shrink-0 shadow-lg shadow-purple-500/30">
              <Moon size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Bedtime, one tap away</p>
              {platform === "ios" ? (
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  Add DreamWeaver to your Home Screen: tap <Share2 size={11} className="inline -mt-0.5 text-slate-300" /> Share, then "Add to Home Screen".
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  Install DreamWeaver for one-tap bedtime stories, right from your home screen.
                </p>
              )}
              <div className="flex items-center gap-3 mt-3">
                {platform === "android" && (
                  <button
                    data-testid="a2hs-install-btn"
                    onClick={handleInstall}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-xs font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                  >
                    <Plus size={12} /> Install
                  </button>
                )}
                <button
                  data-testid="a2hs-dismiss-btn"
                  onClick={dismiss}
                  className="text-xs text-slate-500 hover:text-white transition-colors"
                >
                  {platform === "ios" ? "Got it" : "Not now"}
                </button>
              </div>
            </div>
            <button
              data-testid="a2hs-close-btn"
              onClick={dismiss}
              className="min-h-[32px] min-w-[32px] flex items-center justify-center text-slate-500 hover:text-white transition-colors shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
